// services/api/ai.api.ts
import { api } from "./api";
import {
  TAIResponse,
  TRequestAI,
  ChatMessage,
  ConversationTab,
  DisplayMessage,

} from "../types/ai.types";

export type { ChatMessage, ConversationTab };

/* ========= Types ========= */
export interface UserGoal {
  idGoal: number;
  usersId: number;
  title: string;
  description?: string | null;
  progress: number;
}

type Role = "user" | "assistant" | "system";

type AskGoalsArgs = {
  idGoal?: number; // אם יש יעד קיים – ייכנס ל-URL
  message: string;
  conversationHistory?: { role: Role; content: string }[];
  isEndOfConversation: boolean;
};

export interface HistoryItem {
  role: Role;
  content: string;
}
type SaveGoalsArgs = {
  idGoal?: number; // אם יש יעד קיים – ייכנס ל-URL
  isEndOfConversation: true;
  messages: {
    usersGuid: string;
    role: "user" | "assistant";
    contents: string;
    createdAt?: string;
  }[];
  // אם אין idGoal קיים ורוצים ליצור יעד חדש:
  newGoal?: { title: string; description?: string; progress?: number };
};

type AskGoalsResponse = {
  reply?: string;
  type?: string;
  goal?: string;
  newGoal?: string;
  oldGoal?: string;
  category?: string;
};
export type UpdateGoalArgs = {
  idGoal: number;
  title?: string;
  description?: string | null;
  progress?: number;
  clearHistory?: boolean; // אופציונלי – למחיקת היסטוריה קיימת
};

export type UpdateGoalResponse = {
  updatedGoalId: number;
  removedHistory?: number; // יוחזר אם clearHistory=true
};

type SaveGoalsResponse = { idGoal: number; inserted: number; note: string };

/** invalidate helpers (לשיחות AI הקיימות) */
const getInvalidateTags = (conversationId?: number | null) => {
  const tags: any[] = ["Conversations"];
  if (conversationId) {
    tags.push({ type: "ConversationHistory", id: conversationId });
  }
  return tags;
};

export const aiApi = api.injectEndpoints({
  endpoints: (build) => ({
    /* ====== EXISTING ENDPOINTS ====== */
    getResponseFromAI: build.mutation<
      TAIResponse,
      {
        conversationId?: number | null;
        roleId?: number | null;
        typeId?: number | null;
        Message: string;
        ConversationHistory?: ChatMessage[];
      }
    >({
      query: ({ conversationId, roleId, typeId, Message, ConversationHistory }) => ({
        url: `AI_GetResponseFromAI/${conversationId ?? ""}/${roleId ?? ""}/${typeId ?? ""}`,
        method: "POST",
        body: { Message, ConversationHistory },
      }),
      invalidatesTags: (result, error, { conversationId }) =>
        getInvalidateTags(conversationId),
    }),
    getChatResponseFromAI: build.mutation<
      TAIResponse,
      TRequestAI & { conversationId?: number | null }
    >({
      query: ({ conversationId, ...body }) => {
        const convSegment = conversationId != null ? conversationId.toString() : "new"; // safe
        return {
          url: `ai/AI_GetResponseFromAI/${convSegment}/1/1`,
          method: "POST",
          body,
        };
      },

      async onQueryStarted({ conversationId, message }, { dispatch, queryFulfilled }) {
        if (!message?.trim()) return; // ignore empty messages

        // If conversationId is not defined, just invalidate conversations after query
        if (conversationId == null) {
          try {
            await queryFulfilled;
            dispatch(aiApi.util.invalidateTags(["Conversations"]));
          } catch (err) {
            console.error("[getResponseFromAI] query failed (no conversationId):", err);
          }
          return;
        }

        let patchResult;
        try {
          patchResult = dispatch(
            aiApi.util.updateQueryData(
              "getConversationHistory",
              conversationId,
              (draft: ChatMessage[]) => {
                draft.push({
                  role: "user",
                  content: message,
                });
              }
            )
          );

          await queryFulfilled;
        } catch (err) {
          console.error("[getResponseFromAI] query failed (with conversationId):", err);
          if (patchResult) patchResult.undo(); // revert optimistic update
        }
      },

      invalidatesTags: (result, error, { conversationId }) =>
        conversationId != null
          ? [
            { type: "ConversationHistory", id: conversationId },
            "Conversations",
          ]
          : ["Conversations"],
    }),
    getResponseFromVoiceAI: build.mutation<
      TAIResponse,
      TRequestAI & { conversationId?: number | null }
    >({
      query: ({ conversationId, ...body }) => ({
        url: `AI_GetResponseFromVoiceAI/${conversationId || ""}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { conversationId }) =>
        getInvalidateTags(conversationId),
    }),

    getConversationHistory: build.query<ChatMessage[], number>({
      query: (id) => `AI_GetConversationHistory/${id}`,
      providesTags: (result, error, id) => [{ type: "ConversationHistory", id }],
    }),

    /* ====== GOALS: chat online (לא סוף שיחה) ====== */
    aiGetGoals: build.mutation<AskGoalsResponse, AskGoalsArgs>({
      query: ({ idGoal, message, conversationHistory, isEndOfConversation }) => ({
        url: idGoal != null ? `AI/AI_GetGoals/${idGoal}` : `AI/AI_GetGoals`,
        method: "POST",
        body: { message, conversationHistory, isEndOfConversation },
      }),
    }),

    /* ====== GOALS: save end-of-conversation (יוצר יעד חדש אם צריך) ====== */
    saveGoalsConversation: build.mutation<SaveGoalsResponse, SaveGoalsArgs>({
      query: ({ idGoal, messages, isEndOfConversation, newGoal }) => ({
        url: idGoal != null ? `AI/AI_GetGoals/${idGoal}` : `AI/AI_GetGoals`,
        method: "POST",
        body: {
          isEndOfConversation, // true
          messages,
          idGoal:
            !idGoal && newGoal
              ? {
                title: newGoal.title,
                description: newGoal.description,
                progress: newGoal.progress ?? 0,
              }
              : undefined,
        },
      }),
      // אחרי יצירה/שמירה – נרענן את רשימת המטרות
      invalidatesTags: ["Goals"],
    }),

    /* ====== GOALS: get all by userId ====== */
    getGoalsByUserId: build.query<UserGoal[], void>({
      // שרת: GET /AI_GetGoalsAllGoalsByUserID?usersId=123
      query: () => ({
        url: `AI/AI_GetGoalsAllGoalsByUserID`,
        method: "GET",
      }),
      providesTags: ["Goals"],
    }),
    /* ====== GOALS: update by idGoal ====== */

    updateGoal: build.mutation<UpdateGoalResponse, UpdateGoalArgs>({
      query: ({ idGoal, title, description, progress, clearHistory }) => ({
        url: `AI/goals/${idGoal}`,
        method: "PUT",
        body: {
          title,
          description,
          progress,
          clearHistory,
        },
      }),
      invalidatesTags: ["Goals"],
    }),
    // goals.api.ts או ai.api.ts

    updateGoalProgress: build.mutation<
      { updatedGoalId: number; previousProgress: number; newProgress: number; rationale: string },
      { idGoal?: number; message?: string; conversationHistory: HistoryItem[] }
    >({
      query: (body) => ({
        url: 'ai/AI_FinalizeGoalProgress',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Goals'],
    }),
    /* ====== GOALS: delete by idGoal ====== */
    deleteGoal: build.mutation<{ deletedGoalId: number },  number >(
      {
        query: (idGoal) => ({
          url: `AI/goals/${idGoal}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Goals"],
      }),
      getTranscription: build.mutation<TAIResponse, { file: File }>({
        query: ({ file }) => {
          const formData = new FormData();
          formData.append("file", file);
          return {
            url: `AI/TranscribeAudio`,
            method: "POST",
            body: formData,
          };
        },
      }),
  }),
  overrideExisting: false,
});



/* ====== Hooks export ====== */
export const {
  useGetResponseFromAIMutation,
  useGetChatResponseFromAIMutation,
  useGetResponseFromVoiceAIMutation,
  useGetConversationHistoryQuery,
  useGetTranscriptionMutation,
  useAiGetGoalsMutation,
  useSaveGoalsConversationMutation,
  useGetGoalsByUserIdQuery,    // חשוב: ה-hook לשליפת מטרות
  useDeleteGoalMutation,
  useUpdateGoalMutation,
  useUpdateGoalProgressMutation
} = aiApi;